import { create } from 'zustand';

/** Load from storage */
const loadFromStorage = <T,>(key: string, defaultValue: T): T => {
  const saved = localStorage.getItem(key);
  return saved ? JSON.parse(saved) : defaultValue;
};

/** Save to Storage */
const saveToStorage = <T,>(key: string, value: T) => {
  localStorage.setItem(key, JSON.stringify(value));
};

interface OpenedApp {
  id: string;
  name: string;
}

interface CurrentSlots {
  full: string | null;
  panelA: string | null;
  panelB: string | null;
  drawer: string | null;
}

interface AppSelectionStore {
  openedApps: OpenedApp[];
  setOpenedApps: (open: OpenedApp[]) => void;
  slotSelections: Record<string, { appId: string | null; viewId: string | null }>;
  updateSlotSelection: (
    slot: keyof CurrentSlots,
    appId: string | null,
    viewId?: string | null
  ) => void;

  selectedApp: string;
  selectedAppSetting: string;
  setSelectedApp: (appId: string) => void;
  setSelectedAppSettings: (setting: string) => void;

  selectedAppsAdminView: string;
  setSelectedAppsAdminView: (admin: string) => void;

  selectedAppsReportView: string;
  setSelectedAppsReportView: (report: string) => void;
}

export const useAppSelectionStore = create<AppSelectionStore>((set, get) => ({
  selectedApp: get()?.slotSelections.full?.appId || '',
  selectedAppSetting: '',
  selectedAppsAdminView: '',
  selectedAppsReportView: '',

  openedApps: loadFromStorage('openedApps', []),
  slotSelections: loadFromStorage('slotSelections', {
    full: { appId: null, viewId: null },
    panelA: { appId: null, viewId: null },
    panelB: { appId: null, viewId: null },
  }),

  setSelectedApp: (appId) => set({ selectedApp: appId }),
  setSelectedAppSettings: (setting) => set({ selectedAppSetting: setting }),
  setSelectedAppsAdminView: (adminView) => set({ selectedAppsAdminView: adminView }),
  setSelectedAppsReportView: (reportView) => set({ selectedAppsReportView: reportView }),

  setOpenedApps: (apps: OpenedApp[]) => {
    set((state) => {
      if (!apps || !Array.isArray(apps)) {
        return { openedApps: [...state.openedApps] };
      }

      const updatedApps = [...apps];
      saveToStorage('openedApps', updatedApps);
      return { openedApps: updatedApps };
    });
  },

  updateSlotSelection: (slot: keyof CurrentSlots, appId: string | null, viewId?: string | null) => {
    set((state) => {
      const lastView = viewId || state.slotSelections[slot]?.viewId || 'default';
      let updatedSelections = null;

      if (!appId) {
        updatedSelections = { ...state.slotSelections, [slot]: { appId: null, viewId: null } };
      } else {
        updatedSelections = { ...state.slotSelections, [slot]: { appId, viewId: lastView } };
      }
      saveToStorage('slotSelections', updatedSelections);
      return { slotSelections: updatedSelections };
    });
  },
}));
